/**
 * Portal Color System
 *
 * Centralized color definitions for each portal (Admin, Business, Consumer).
 * Supports multiple color templates for user customization.
 *
 * Default themes:
 * - Admin Portal: Gold (premium, authoritative)
 * - Business Portal: Blue (Outlook-inspired, professional)
 * - Consumer Portal: Green (Excel-inspired, trustworthy)
 */

// ============================================================================
// Color Scale Type
// ============================================================================

export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string; // Main/default shade
  600: string;
  700: string;
  800: string;
  900: string;
}

export interface PortalColorPalette {
  primary: ColorScale;
  primaryDark: ColorScale; // For dark mode adjustments
  // Semantic colors derived from primary
  accent: string;
  accentLight: string;
  // Glow colors for effects
  glowSm: string;
  glowMd: string;
  glowLg: string;
  // Dark mode glow
  glowSmDark: string;
  glowMdDark: string;
  glowLgDark: string;
}

// ============================================================================
// Admin Portal Colors - Gold Theme
// ============================================================================

export const adminGoldPalette: PortalColorPalette = {
  primary: {
    50: '#FDF8E8',
    100: '#FAF0C8',
    200: '#F5E08F',
    300: '#EFCF56',
    400: '#E8BD2D',
    500: '#D4A017', // Main gold - rich, refined
    600: '#B8860B',
    700: '#9A7209',
    800: '#7D5C07',
    900: '#5C4305',
  },
  primaryDark: {
    50: '#FEF9E7',
    100: '#FCF3CF',
    200: '#F9E79F',
    300: '#F4D35E',
    400: '#E8BD2D',
    500: '#F4D35E', // Brighter gold for dark mode
    600: '#D4A017',
    700: '#B8860B',
    800: '#9A7209',
    900: '#7D5C07',
  },
  accent: '#B8860B',
  accentLight: '#FAF0C8',
  // Light mode glows
  glowSm: 'rgba(212, 160, 23, 0.15)',
  glowMd: 'rgba(212, 160, 23, 0.25)',
  glowLg: 'rgba(212, 160, 23, 0.35)',
  // Dark mode glows
  glowSmDark: 'rgba(244, 211, 94, 0.2)',
  glowMdDark: 'rgba(244, 211, 94, 0.35)',
  glowLgDark: 'rgba(244, 211, 94, 0.45)',
};

// ============================================================================
// Business Portal Colors — OpsMatrix Mint Theme
// ============================================================================
//
// Retuned twice from the legacy Outlook-blue palette. The current target is
// the punchier mint #4ed9a4 from the UX v3.1 handoff (`GK26HXoXPwqVYf1u300Z6w`,
// "stronger green") with #7fdbb6 used as the "done" / mint-soft variant on
// stage indicators. The previous #4cd5b0 retune (handoff `_3xZaHe5no…`) was
// close but the design team explicitly pushed it warmer + more saturated.
// Variable name retained for backward compatibility with existing imports;
// `opsmatrixMintPalette` is exported as the semantic alias below.
//
export const businessBluePalette: PortalColorPalette = {
  primary: {
    50: '#E6FBF2',
    100: '#C5F4DF',
    200: '#9EEBC9',
    300: '#7FDBB6', // mintSoft — v3.1 done/secondary state
    400: '#62D9AC',
    500: '#4ED9A4', // Main mint — v3.1 primary
    600: '#3FBA8A',
    700: '#319770',
    800: '#237356',
    900: '#155039',
  },
  primaryDark: {
    50: '#E6FBF2',
    100: '#C5F4DF',
    200: '#9EEBC9',
    300: '#7FDBB6', // design-bundle --accent-2 (mintSoft)
    400: '#62D9AC',
    500: '#4ED9A4', // Same main on dark — single-color system
    600: '#3FBA8A',
    700: '#319770',
    800: '#237356',
    900: '#155039',
  },
  accent: '#4ED9A4',
  accentLight: '#C5F4DF',
  // Light mode glows
  glowSm: 'rgba(78, 217, 164, 0.18)',
  glowMd: 'rgba(78, 217, 164, 0.30)',
  glowLg: 'rgba(78, 217, 164, 0.45)',
  // Dark mode glows
  glowSmDark: 'rgba(78, 217, 164, 0.22)',
  glowMdDark: 'rgba(78, 217, 164, 0.35)',
  glowLgDark: 'rgba(78, 217, 164, 0.50)',
};

/** Semantic alias — prefer this name in new code. */
export const opsmatrixMintPalette = businessBluePalette;

// ============================================================================
// Consumer Portal Colors - Excel Green Theme
// ============================================================================

export const consumerGreenPalette: PortalColorPalette = {
  primary: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#34D399',
    500: '#16A34A', // Main green - vibrant Excel-inspired
    600: '#15803D',
    700: '#166534',
    800: '#14532D',
    900: '#052E16',
  },
  primaryDark: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#4ADE80',
    500: '#4ADE80', // Brighter green for dark mode
    600: '#22C55E',
    700: '#16A34A',
    800: '#15803D',
    900: '#166534',
  },
  accent: '#15803D',
  accentLight: '#D1FAE5',
  // Light mode glows
  glowSm: 'rgba(22, 163, 74, 0.15)',
  glowMd: 'rgba(22, 163, 74, 0.25)',
  glowLg: 'rgba(22, 163, 74, 0.35)',
  // Dark mode glows
  glowSmDark: 'rgba(74, 222, 128, 0.2)',
  glowMdDark: 'rgba(74, 222, 128, 0.35)',
  glowLgDark: 'rgba(74, 222, 128, 0.45)',
};

// ============================================================================
// Alternative Templates (User Selectable)
// ============================================================================

// Purple/Violet theme (premium feel)
export const purplePalette: PortalColorPalette = {
  primary: {
    50: '#F5F3FF',
    100: '#EDE9FE',
    200: '#DDD6FE',
    300: '#C4B5FD',
    400: '#A78BFA',
    500: '#8B5CF6', // Main purple
    600: '#7C3AED',
    700: '#6D28D9',
    800: '#5B21B6',
    900: '#4C1D95',
  },
  primaryDark: {
    50: '#F5F3FF',
    100: '#EDE9FE',
    200: '#DDD6FE',
    300: '#C4B5FD',
    400: '#A78BFA',
    500: '#A78BFA',
    600: '#8B5CF6',
    700: '#7C3AED',
    800: '#6D28D9',
    900: '#5B21B6',
  },
  accent: '#7C3AED',
  accentLight: '#EDE9FE',
  glowSm: 'rgba(139, 92, 246, 0.15)',
  glowMd: 'rgba(139, 92, 246, 0.25)',
  glowLg: 'rgba(139, 92, 246, 0.35)',
  glowSmDark: 'rgba(167, 139, 250, 0.2)',
  glowMdDark: 'rgba(167, 139, 250, 0.35)',
  glowLgDark: 'rgba(167, 139, 250, 0.45)',
};

// Teal theme (modern, fresh)
export const tealPalette: PortalColorPalette = {
  primary: {
    50: '#F0FDFA',
    100: '#CCFBF1',
    200: '#99F6E4',
    300: '#5EEAD4',
    400: '#2DD4BF',
    500: '#14B8A6', // Main teal
    600: '#0D9488',
    700: '#0F766E',
    800: '#115E59',
    900: '#134E4A',
  },
  primaryDark: {
    50: '#F0FDFA',
    100: '#CCFBF1',
    200: '#99F6E4',
    300: '#5EEAD4',
    400: '#2DD4BF',
    500: '#2DD4BF',
    600: '#14B8A6',
    700: '#0D9488',
    800: '#0F766E',
    900: '#115E59',
  },
  accent: '#0D9488',
  accentLight: '#CCFBF1',
  glowSm: 'rgba(20, 184, 166, 0.15)',
  glowMd: 'rgba(20, 184, 166, 0.25)',
  glowLg: 'rgba(20, 184, 166, 0.35)',
  glowSmDark: 'rgba(45, 212, 191, 0.2)',
  glowMdDark: 'rgba(45, 212, 191, 0.35)',
  glowLgDark: 'rgba(45, 212, 191, 0.45)',
};

// Rose/Pink theme (energetic)
export const rosePalette: PortalColorPalette = {
  primary: {
    50: '#FFF1F2',
    100: '#FFE4E6',
    200: '#FECDD3',
    300: '#FDA4AF',
    400: '#FB7185',
    500: '#F43F5E', // Main rose
    600: '#E11D48',
    700: '#BE123C',
    800: '#9F1239',
    900: '#881337',
  },
  primaryDark: {
    50: '#FFF1F2',
    100: '#FFE4E6',
    200: '#FECDD3',
    300: '#FDA4AF',
    400: '#FB7185',
    500: '#FB7185',
    600: '#F43F5E',
    700: '#E11D48',
    800: '#BE123C',
    900: '#9F1239',
  },
  accent: '#E11D48',
  accentLight: '#FFE4E6',
  glowSm: 'rgba(244, 63, 94, 0.15)',
  glowMd: 'rgba(244, 63, 94, 0.25)',
  glowLg: 'rgba(244, 63, 94, 0.35)',
  glowSmDark: 'rgba(251, 113, 133, 0.2)',
  glowMdDark: 'rgba(251, 113, 133, 0.35)',
  glowLgDark: 'rgba(251, 113, 133, 0.45)',
};

// Slate/Neutral theme (minimal, professional)
export const slatePalette: PortalColorPalette = {
  primary: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B', // Main slate
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  primaryDark: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#94A3B8',
    600: '#64748B',
    700: '#475569',
    800: '#334155',
    900: '#1E293B',
  },
  accent: '#475569',
  accentLight: '#F1F5F9',
  glowSm: 'rgba(100, 116, 139, 0.15)',
  glowMd: 'rgba(100, 116, 139, 0.25)',
  glowLg: 'rgba(100, 116, 139, 0.35)',
  glowSmDark: 'rgba(148, 163, 184, 0.2)',
  glowMdDark: 'rgba(148, 163, 184, 0.35)',
  glowLgDark: 'rgba(148, 163, 184, 0.45)',
};

// ============================================================================
// Template Registry
// ============================================================================

export type PortalColorTemplate = 'gold' | 'blue' | 'green' | 'purple' | 'teal' | 'rose' | 'slate';

export const portalColorTemplates: Record<PortalColorTemplate, PortalColorPalette> = {
  gold: adminGoldPalette,
  blue: businessBluePalette,
  green: consumerGreenPalette,
  purple: purplePalette,
  teal: tealPalette,
  rose: rosePalette,
  slate: slatePalette,
};

export const templateLabels: Record<PortalColorTemplate, string> = {
  gold: 'Gold',
  blue: 'Outlook Blue',
  green: 'Excel Green',
  purple: 'Violet',
  teal: 'Teal',
  rose: 'Rose',
  slate: 'Slate',
};

export const templateDescriptions: Record<PortalColorTemplate, string> = {
  gold: 'Premium gold theme with warm, authoritative tones',
  blue: 'Professional blue inspired by Microsoft Outlook',
  green: 'Fresh green inspired by Microsoft Excel',
  purple: 'Elegant purple theme with a premium feel',
  teal: 'Modern teal theme that feels fresh and innovative',
  rose: 'Energetic rose theme with vibrant personality',
  slate: 'Minimal slate theme for a clean, professional look',
};

// ============================================================================
// Portal Default Templates
// ============================================================================

export type PortalType = 'admin' | 'business' | 'consumer';

export const portalDefaultTemplates: Record<PortalType, PortalColorTemplate> = {
  admin: 'gold',
  business: 'blue',
  consumer: 'green',
};

// ============================================================================
// CSS Variable Generator
// ============================================================================

/**
 * Generate CSS custom properties for a portal color palette.
 * Can be injected into :root or a specific element.
 */
export function generatePortalCSSVariables(palette: PortalColorPalette, isDark = false): Record<string, string> {
  const primary = isDark ? palette.primaryDark : palette.primary;
  const glow = isDark
    ? { sm: palette.glowSmDark, md: palette.glowMdDark, lg: palette.glowLgDark }
    : { sm: palette.glowSm, md: palette.glowMd, lg: palette.glowLg };

  return {
    '--color-primary': primary[500],
    '--color-primary-50': primary[50],
    '--color-primary-100': primary[100],
    '--color-primary-200': primary[200],
    '--color-primary-300': primary[300],
    '--color-primary-400': primary[400],
    '--color-primary-500': primary[500],
    '--color-primary-600': primary[600],
    '--color-primary-700': primary[700],
    '--color-primary-800': primary[800],
    '--color-primary-900': primary[900],
    '--color-primary-light': primary[100],
    '--color-primary-lighter': primary[50],
    '--color-primary-dark': primary[700],
    '--color-accent': palette.accent,
    '--color-accent-light': palette.accentLight,
    '--glow-primary-sm': glow.sm,
    '--glow-primary-md': glow.md,
    '--glow-primary-lg': glow.lg,
    '--shadow-glow': `0 0 20px ${glow.md}`,
    '--shadow-glow-sm': `0 0 15px ${glow.sm}`,
    '--shadow-glow-md': `0 0 25px ${glow.md}`,
    '--shadow-glow-lg': `0 0 35px ${glow.lg}`,
  };
}

/**
 * Get the color palette for a template
 */
export function getPortalPalette(template: PortalColorTemplate): PortalColorPalette {
  return portalColorTemplates[template];
}

/**
 * Get the default template for a portal type
 */
export function getDefaultTemplate(portal: PortalType): PortalColorTemplate {
  return portalDefaultTemplates[portal];
}

/**
 * Get all available templates
 */
export function getAvailableTemplates(): PortalColorTemplate[] {
  return Object.keys(portalColorTemplates) as PortalColorTemplate[];
}
